import {
  LitElement,
  html,
} from "https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js";

/**
 * "Ejects" from the shadow DOM.
 * Advantage: The element and it's children will be styled according to (cascading) global style selectors.
 * Disadvantage: slots won't work within these elements.
 */
class LitElementEjected extends LitElement {
  createRenderRoot() {
    // Returning `this` will render the template directly into the light DOM
    return this;
  }
}

export class SubscriptionWidget extends LitElementEjected {
  static properties = {
    productId: { type: String },
    variantId: { type: String },
    formId: { type: String },
    _product: { type: Object, state: true },
    _variants: { type: Array, state: true },
    _selectedVariant: { type: Object, state: true },
    _selectedOption: { type: String, state: true },
    _selectedPlan: { type: Object, state: true },
    _plans: { type: Array, state: true },
    _loadingState: { type: String, state: true },
    _quantity: { type: Number, state: true },
    _slashedPrice: { type: String, state: true },
    _actualPrice: { type: String, state: true },
  };

  constructor() {
    super();
    this.productId = null;
    this.variantId = null;
    this.formId = null;
    this._product = null;
    this._variants = [];
    this._selectedVariant = null;
    this._selectedPlan = null;
    this._plans = [];
    this._loadingState = "idle";
    this._selectedOption = null;
    this._slashedPrice = undefined;
    this._actualPrice = undefined;
    this._quantity = 1;
  }

  connectedCallback() {
    super.connectedCallback();
    this._init();
  }

  updated(changedProps) {
    //update pricing when variant id changes
    if (changedProps.has("variantId")) {
      if (
        changedProps.get("variantId") !== undefined &&
        this._loadingState === "idle"
      ) {
        this._setupProductState();
      }
    }
  }

  async _init() {
    if (!this.productId || !this.variantId) {
      console.error("Product ID or Variant ID is missing");
      return;
    }

    this._loadingState = "loading";

    try {
      await this._fetchProductData();
      this._loadingState = "idle";
    } catch (error) {
      console.error("Error initializing widget:", error);
      this._loadingState = "error";
    }
  }

  async _fetchProductData() {
    const session = await recharge.auth.loginShopifyAppProxy();
    const settings = await recharge.cdn.getCDNProductAndSettings(
      this.productId
    );

    const product_settings = settings.product;
    const productQuery = await recharge.product.productSearch(session, {
      format_version: "2022-06",
      external_product_ids: [this.productId],
    });

    if (!productQuery.products || productQuery.products.length === 0) {
      throw new Error("Product not found");
    }

    this._product = productQuery.products[0];
    this._variants = product_settings.variants.filter(
      (v) => v.is_published !== false
    );

    this._setupProductState();
  }

  _setupProductState() {
    this._selectedVariant = this._variants.find(
      (v) => Number(v.id) === Number(this.variantId)
    );

    if (!this._selectedVariant) {
      throw new Error("Selected variant not found");
    }

    this._plans = this._product.plans
      .filter((plan) => plan.type !== "onetime")
      .sort((a, b) => a.sort_order - b.sort_order);

    if (customerTags) {
      //special tags that allow access to weekly plans
      const specialTags = ["MON South", "TUE North", "THU Central"];

      //check if customer has any of the special tags
      const hasSpecialAccess = customerTags.some((tag) =>
        specialTags.includes(tag)
      );

      //finding the weekly plan
      this._plans = this._plans.filter((plan) => {
        const planFrequency = plan.external_plan_name.toLowerCase();

        //if customer has special access, they can see all plans
        if (hasSpecialAccess) {
          return true;
        }

        //if customer doesn't have special access, they can only see bi-weekly or less frequent plans
        //this means they CAN'T see weekly plans
        return !planFrequency.includes("every week");
      });
    }

    this._selectedPlan = this._plans.length > 0 ? this._plans[0] : null;

    if (this._selectedPlan) {
      this._selectedOption = "subscriptions";
      this._updatePricing();
    } else {
      this._selectedOption = "one-time-purchase";
    }
  }

  _updatePricing() {
    if (!this._selectedVariant || !this._selectedPlan) return;

    for (const plan of this._plans) {
      if (!this._selectedVariant.selling_plan_allocations) continue;

      const allocation = this._selectedVariant.selling_plan_allocations.find(
        (price) =>
          price.selling_plan_id.toString() === plan.external_plan_id.toString()
      );

      if (allocation) {
        plan.actualPrice = `$${allocation.discounted_price}`;
        plan.slashedPrice = `$${this._selectedVariant.prices.unit_price}`;
      } else {
        plan.actualPrice = `$${this._selectedVariant.prices.unit_price}`;
      }
    }

    const sellingPlanInput = document.querySelector(
      'input[type="hidden"][name="selling_plan"]'
    );

    if (!sellingPlanInput) {
      console.error("Could not find the selling_plan input element");
      return null;
    }

    if (this._selectedOption === "subscriptions" && this._selectedPlan) {
      this._actualPrice = this._selectedPlan.actualPrice;
      this._slashedPrice = this._selectedPlan.slashedPrice;
      if (sellingPlanInput)
        sellingPlanInput.value = this._selectedPlan.external_plan_id;
    } else {
      this._actualPrice = `$${this._selectedVariant.prices.unit_price}`;
      this._slashedPrice = undefined;
      if (sellingPlanInput) sellingPlanInput.value = "";
    }
  }

  _handleRadioChange(event) {
    this._selectedOption = event.target.value;
    this._updatePricing();
    this.requestUpdate();
  }

  _updateFrequency(event) {
    const planId = Number(event.target.value);
    this._selectedPlan = this._plans.find((p) => Number(p.id) === planId);
    this.requestUpdate();
  }

  _handleSellingPlanChange(event) {
    const selectedPlanId = event.target.value;
    this._selectedPlan = this._plans.find(
      (plan) => plan.external_plan_id === Number(selectedPlanId)
    );
    this._updatePricing();
    this.requestUpdate();
  }

  _renderFrequencyOption(plan) {
    if (
      plan.external_variant_ids.length > 0 &&
      !plan.external_variant_ids.find(
        (id) => Number(id.ecommerce) === Number(this.variant_id)
      )
    )
      return;

    return html`
      <div class="sms-rw__radio-wrapper">
        <input
          name="selling_plan"
          id="${plan.id}"
          type="radio"
          .value=${plan.external_plan_id}
          data-interval="${plan.subscription_preferences
            .order_interval_frequency}"
          ?checked=${this._selectedPlan && this._selectedPlan.id === plan.id}
          class="sms-rw__radio-option"
          @change=${this._handleSellingPlanChange}
          form="${this.formId}"
          tabindex="0"
        />
        <label class="sms-rw__radio-option-label" for="${plan.id}">
          ${plan.title}
        </label>
      </div>
    `;
  }

  render() {
    if (this._loadingState === "loading") {
      return html`<div class="sms-recharge-widget__loading">Loading...</div>`;
    }

    if (
      this._loadingState === "error" ||
      !this._product ||
      !this._selectedVariant
    ) {
      return html`<div class="sms-recharge-widget__error">
        Unable to load subscription options
      </div>`;
    }

    const showSubscriptionOption = this._plans && this._plans.length > 0;

    return html`
      <div class="sms-recharge-widget">
        <div
          class="sms-recharge-widget__radio-option ${this._selectedOption ===
          "one-time-purchase"
            ? "sms-rw__radio-selected"
            : "sms-rw__radio-unselected"}"
        >
          <div class="sms-rw__radio-label-container">
            <label class="sms-rw__radio-label-left">
              <input
                type="radio"
                id="${this.productId}-one-time-purchase"
                name="purchase-option-${this.productId}"
                value="one-time-purchase"
                @change=${this._handleRadioChange}
                ?checked=${this._selectedOption === "one-time-purchase"}
                class="sms-rw__radio-label-input"
                form="${this.formId}"
              />
              <span class="sms-rw__radio-label-text">One-time purchase</span>
            </label>

            <div class="sms-rw__actual-price">
              $${this._selectedVariant.prices.unit_price}
            </div>
          </div>
        </div>

        ${showSubscriptionOption
          ? html`
              <div
                class="sms-recharge-widget__radio-option ${this
                  ._selectedOption === "subscriptions"
                  ? "sms-rw__radio-selected"
                  : "sms-rw__radio-unselected"}"
              >
                <div class="sms-rw__save-tag">Save up to 10%</div>

                <div class="sms-rw__radio-label-container">
                  <label class="sms-rw__radio-label-left">
                    <input
                      type="radio"
                      id="${this.productId}-subscriptions"
                      name="purchase-option-${this.productId}"
                      value="subscriptions"
                      @change=${this._handleRadioChange}
                      ?checked=${this._selectedOption === "subscriptions"}
                      class="sms-rw__radio-label-input"
                      form="${this.formId}"
                    />
                    <span class="sms-rw__radio-label-text"
                      >Subscribe and Save</span
                    >
                  </label>
                  <div>
                    <span class="sr-only">original price</span>
                    <div class="sms-rw__slashed-price sms-rw__line-height">
                      ${this._slashedPrice}
                    </div>
                    <span class="sr-only">discounted subscription price</span>
                    <div class="sms-rw__actual-price">${this._actualPrice}</div>
                  </div>
                </div>

                ${this._selectedOption === "subscriptions"
                  ? html`
                      <div class="sms-widget__select-wrapper">
                        <div class="sms-rw__bold-text">Deliver every:</div>

                        <div class="sms-rw__options-container">
                          ${this._plans.map((plan) =>
                            this._renderFrequencyOption(plan)
                          )}
                        </div>
                      </div>
                    `
                  : ""}
              </div>
            `
          : ""}
      </div>
    `;
  }
}

customElements.define("sms-recharge-widget", SubscriptionWidget);
